import React from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Button } from '@michelin/acid-components';
import { usePermissions, useTranslation } from '@michelin/central-provider';
import { ContactListingOVM } from '@michelin/fcp-view-models';
import { getNotificationSubscriptionLabelFromValue } from 'components/Contact/ContactDetails/utils';
import { TranslateCallback } from 'components/Util';
import { ContactListingWithNotifications, useContactsList } from 'hooks/useContacts';
import { canUserEditByHierarchy } from 'modules/Contacts/utils';
import { getBaseUrl } from 'prefs-and-service-offers';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { AreaHeader, OpenFullButton, Panel, StatusTone, WhyItMatters } from './components';

// Mirrors components/Contact/ContactDetails/utils.tsx. Kept as local literals
// (rather than importing that GraphQL-heavy module) since this preview page
// only needs the raw string values to identify roles.
const ERS_MANAGER_ROLE_AREA_VALUE = 'ers_manager';
const PO_ISSUER_ROLE_AREA_VALUE = 'po_issuer';
const PRIMARY_LEVEL_VALUE = 'primary';
const TIRE_SERVICE_VALUE = 'tire';
const MECH_SERVICE_VALUE = 'mechanical';

// --- Extraction helpers ------------------------------------------------------
function hasRoleAreaAtLevelForService(
  contact: ContactListingOVM,
  roleArea: string,
  level: string,
  service: string,
  locationHashKey?: string,
): boolean {
  return !!contact.contact_types?.some(
    (contactType) =>
      contactType.service === service &&
      contactType.role_areas?.some(
        (contactTypeRoleArea) =>
          contactTypeRoleArea.role_area === roleArea &&
          contactTypeRoleArea.levels?.some(
            (contactTypeLevel) =>
              contactTypeLevel.level === level &&
              (typeof locationHashKey === 'undefined' || contactTypeLevel.location === locationHashKey),
          ),
      ),
  );
}

function findPrimaryContactForRoleAreaAndService(
  contacts: ContactListingOVM[],
  roleArea: string,
  service: string,
  locationHashKey?: string,
): ContactListingOVM | undefined {
  return contacts.find((contact) =>
    hasRoleAreaAtLevelForService(contact, roleArea, PRIMARY_LEVEL_VALUE, service, locationHashKey),
  );
}

function formatContactName(contact: ContactListingOVM): string {
  const firstInitial = contact.first_name?.charAt(0);
  return firstInitial ? `${firstInitial}. ${contact.last_name}` : contact.last_name ?? '';
}

function formatPhoneNumber(rawPhone?: string): string | undefined {
  if (!rawPhone) return undefined;
  const digits = rawPhone.replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length !== 10) return rawPhone;
  return `+1 (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

function buildServicesSummaryLabel(contact: ContactListingOVM, t: TranslateCallback): string {
  const relevantContactTypes =
    contact.contact_types?.filter((contactType) =>
      contactType.role_areas?.some(
        (contactTypeRoleArea) =>
          contactTypeRoleArea.role_area === ERS_MANAGER_ROLE_AREA_VALUE ||
          contactTypeRoleArea.role_area === PO_ISSUER_ROLE_AREA_VALUE,
      ),
    ) ?? [];
  const hasTire = relevantContactTypes.some((contactType) => contactType.service === TIRE_SERVICE_VALUE);
  const hasMechanical = relevantContactTypes.some((contactType) => contactType.service === MECH_SERVICE_VALUE);

  let label = t('No services assigned');
  if (hasTire && hasMechanical) label = t('Tire & Mechanical');
  else if (hasTire) label = t('Tire');
  else if (hasMechanical) label = t('Mechanical');

  const amounts: string[] = [];
  if (hasTire) amounts.push(`${contact.num_of_tires ?? 0} ${t('tires')} / $${contact.tire_dollar_amount ?? 0}`);
  if (hasMechanical) amounts.push(`$${contact.mechanical_dollar_amount ?? 0}`);

  return amounts.length ? `${label} - ${amounts.join(' / ')}` : label;
}

function buildAssignedLocationsLabel(contact: ContactListingWithNotifications, t: TranslateCallback): string {
  const locations =
    contact.locations
      ?.filter((location) => !location.is_deleted)
      .map((location) => location.location?.customer_name || location.location?.customer_number || location.hash_key)
      .filter(Boolean) ?? [];

  return locations.length ? locations.join(', ') : t('No assigned locations');
}

function getReadableNotificationLabel(value: string, t: TranslateCallback): string {
  return getNotificationSubscriptionLabelFromValue(value, t).replace(/_/g, ' ');
}

function buildNotificationPreferencesLabels(contact: ContactListingWithNotifications, t: TranslateCallback): string[] {
  return (
    contact.notification_subscription?.map((subscription) => {
      const event = subscription.event ? getReadableNotificationLabel(subscription.event, t) : t('Not specified');
      const type = subscription.type ? getReadableNotificationLabel(subscription.type, t) : t('Not specified');
      const methods = subscription.method?.length
        ? subscription.method.map((method) => getReadableNotificationLabel(method, t)).join(', ')
        : t('Not specified');

      return `${t('Event')}: ${event} | ${t('Type')}: ${type} | ${t('Method')}: ${methods}`;
    }) ?? []
  );
}

interface ContactCardData {
  id: string;
  canEdit: boolean;
  role: string;
  roleColorBg: string;
  roleColorFg: string;
  name: string;
  lines: { text: string; tone: 'ok' | 'muted' | 'danger' }[];
  notificationPreferences: string[];
  footer: { text: string; ok: boolean };
}

interface RoleDefinition {
  roleArea: string;
  roleLabel: string;
  roleIcon: string;
  roleColorBg: string;
  roleColorFg: string;
  missingPhoneMessage: string;
  missingEmailMessage: string;
}

interface ServiceSection {
  service: string;
  title: string;
  emptyMessage: string;
}

const ROLE_DEFINITIONS: RoleDefinition[] = [
  {
    roleArea: ERS_MANAGER_ROLE_AREA_VALUE,
    roleLabel: 'ERS Manager - Primary',
    roleIcon: '\u26A1',
    roleColorBg: '#fff4cc',
    roleColorFg: '#8a6d00',
    missingPhoneMessage: 'Add phone to enable after-hours ERS calls',
    missingEmailMessage: 'Add email to receive dispatch notifications',
  },
  {
    roleArea: PO_ISSUER_ROLE_AREA_VALUE,
    roleLabel: 'PO Issuer - Primary',
    roleIcon: '\u{1F4B3}',
    roleColorBg: '#e0ecff',
    roleColorFg: '#1a4a8a',
    missingPhoneMessage: 'Add phone to reach this contact for billing questions',
    missingEmailMessage: 'Add email to receive invoices and POs',
  },
];

const SERVICE_SECTIONS: ServiceSection[] = [
  {
    service: TIRE_SERVICE_VALUE,
    title: 'Primary Tire Contacts',
    emptyMessage: 'No primary {{service}} contact assigned for this role yet.',
  },
  {
    service: MECH_SERVICE_VALUE,
    title: 'Primary Mechanical Contacts',
    emptyMessage: 'No primary {{service}} contact assigned for this role yet.',
  },
];

function getServiceLabel(service: string): string {
  if (service === TIRE_SERVICE_VALUE) return 'Tire';
  if (service === MECH_SERVICE_VALUE) return 'Mechanical';
  return service;
}

function buildContactCardData(
  contact: ContactListingWithNotifications,
  role: RoleDefinition,
  canEdit: boolean,
  t: TranslateCallback,
): ContactCardData {
  const formattedPhone = formatPhoneNumber(contact.cell_phone);
  const hasEmail = !!contact.email_address;
  const hasPhone = !!formattedPhone;

  const lines: ContactCardData['lines'] = [
    hasEmail
      ? { text: `\u2713 ${contact.email_address}`, tone: 'ok' }
      : { text: `\u2717 ${t('Email missing')}`, tone: 'danger' },
    hasPhone
      ? { text: `\u2713 ${formattedPhone}`, tone: 'ok' }
      : { text: `\u2717 ${t('Phone missing')}`, tone: 'danger' },
    { text: buildServicesSummaryLabel(contact, t), tone: 'muted' },
    { text: `${t('Assigned locations')}: ${buildAssignedLocationsLabel(contact, t)}`, tone: 'muted' },
  ];

  let footer: ContactCardData['footer'] = { text: 'Contact is complete', ok: true };
  if (!hasPhone) footer = { text: role.missingPhoneMessage, ok: false };
  else if (!hasEmail) footer = { text: role.missingEmailMessage, ok: false };

  return {
    id: contact.id || '',
    canEdit,
    role: `${role.roleIcon} ${t(role.roleLabel)}`,
    roleColorBg: role.roleColorBg,
    roleColorFg: role.roleColorFg,
    name: formatContactName(contact),
    lines,
    notificationPreferences: buildNotificationPreferencesLabels(contact, t),
    footer: { text: t(footer.text), ok: footer.ok },
  };
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1rem',
    },
    contactCard: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 10,
      padding: '1rem 1.1rem',
      backgroundColor: theme.palette.background.paper,
    },
    contactCardEmpty: {
      border: `1px dashed ${theme.palette.divider}`,
      borderRadius: 10,
      padding: '1rem 1.1rem',
      backgroundColor: theme.palette.action.hover,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '0.6rem',
    },
    roleTag: {
      display: 'inline-block',
      fontSize: '0.65rem',
      fontWeight: 700,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      padding: '3px 8px',
      borderRadius: 5,
      marginBottom: '0.6rem',
    },
    contactName: {
      fontSize: '1rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
      marginBottom: '0.4rem',
    },
    detailLine: {
      fontSize: '0.83rem',
      color: theme.palette.text.primary,
      marginTop: 2,
    },
    detailMuted: {
      fontSize: '0.83rem',
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    detailDanger: {
      fontSize: '0.83rem',
      color: '#c0392b',
      marginTop: 2,
    },
    notificationSummary: {
      borderTop: `1px solid ${theme.palette.divider}`,
      marginTop: '0.75rem',
      paddingTop: '0.65rem',
    },
    notificationSummaryTitle: {
      fontSize: '0.75rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
      marginBottom: '0.25rem',
    },
    notificationSummaryItem: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    emptyMessage: {
      fontSize: '0.83rem',
      color: theme.palette.text.secondary,
    },
    statusOk: {
      fontSize: '0.82rem',
      fontWeight: 600,
      color: '#1e7e34',
      marginTop: '0.5rem',
    },
    statusWarn: {
      fontSize: '0.82rem',
      fontWeight: 600,
      color: '#c0392b',
      marginTop: '0.5rem',
    },
    addContactWrapper: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    cardActions: {
      marginTop: '0.6rem',
      display: 'flex',
      justifyContent: 'flex-end',
    },
  }),
);

export function useContactsAreaStatus() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { selectedAccount } = useParams<{ selectedAccount: string }>();
  const fleetId = permissions.location?.customer_number || selectedAccount;
  const locationHashKey = permissions.location?.hash_key;
  const customerType = permissions.location?.customer_type;
  const updatePermission = permissions.allowsAction('contacts.update');

  const { data: contacts = [], refetch: refetchContacts } = useContactsList(
    fleetId,
    [ERS_MANAGER_ROLE_AREA_VALUE, PO_ISSUER_ROLE_AREA_VALUE],
    customerType,
  );

  const serviceSections = SERVICE_SECTIONS.map((section) => {
    const cards = ROLE_DEFINITIONS.map((role) => {
      const contact = findPrimaryContactForRoleAreaAndService(
        contacts,
        role.roleArea,
        section.service,
        locationHashKey,
      );
      return {
        role,
        service: section.service,
        data: contact
          ? buildContactCardData(contact, role, canUserEditByHierarchy(contact, updatePermission), t)
          : undefined,
      };
    });

    return {
      ...section,
      cards,
    };
  });

  const incompleteCount = serviceSections
    .flatMap((section) => section.cards)
    .filter(({ data }) => !data || !data.footer.ok).length;
  const headerStatus: { label: string; tone: StatusTone } =
    incompleteCount > 0
      ? { label: t('{{count}} incomplete', { count: incompleteCount }), tone: 'warning' }
      : { label: t('Complete'), tone: 'success' };

  const refreshStatus = React.useCallback(() => {
    return refetchContacts();
  }, [refetchContacts]);

  return { serviceSections, incompleteCount, headerStatus, refreshStatus };
}

export function ContactsSubArea() {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const permissions = usePermissions();
  const canCreateContact = permissions.allowsAction('contacts.create');
  const { serviceSections, headerStatus } = useContactsAreaStatus();

  const openFullContacts = () => history.push(`${getBaseUrl()}/contacts`);
  const openCreateContact = () => history.push(`${getBaseUrl()}/contacts/create`);
  const openEditContact = (contactId: string) => history.push(`${getBaseUrl()}/contacts/${contactId}/edit`);

  const toneClass = (tone: 'ok' | 'muted' | 'danger') => {
    if (tone === 'danger') return classes.detailDanger;
    if (tone === 'muted') return classes.detailMuted;
    return classes.detailLine;
  };

  const addContactButton = () => {
    const button = (
      <Button
        variant="contained"
        size="small"
        style={{ textTransform: 'none' }}
        disabled={!canCreateContact}
        onClick={canCreateContact ? openCreateContact : undefined}
      >
        + {t('Add Contact')}
      </Button>
    );

    if (!canCreateContact) {
      return (
        <Tooltip title={t('No permission to add contacts')!} arrow>
          <span className={classes.addContactWrapper}>{button}</span>
        </Tooltip>
      );
    }

    return <span className={classes.addContactWrapper}>{button}</span>;
  };

  return (
    <>
      <AreaHeader
        icon={<span aria-hidden>&#128101;</span>}
        iconColor="#f3d9e6"
        title={t('Contacts')}
        subtitle={t('Who you want making the important decisions')}
        status={headerStatus}
      />

      {serviceSections.map((section) => (
        <Panel key={section.service} title={t(section.title)} action={addContactButton()}>
          <div className={classes.cardsGrid}>
            {section.cards.map(({ role, data }) =>
              data ? (
                <div className={classes.contactCard} key={`${section.service}-${role.roleArea}`}>
                  <span
                    className={classes.roleTag}
                    style={{ backgroundColor: data.roleColorBg, color: data.roleColorFg }}
                  >
                    {data.role}
                  </span>
                  <Typography className={classes.contactName}>{data.name}</Typography>
                  {data.lines.map((line) => (
                    <Typography key={line.text} className={toneClass(line.tone)}>
                      {line.text}
                    </Typography>
                  ))}
                  <div className={classes.notificationSummary}>
                    <Typography className={classes.notificationSummaryTitle}>
                      {t('Notification Preferences')}
                    </Typography>
                    {data.notificationPreferences.length ? (
                      data.notificationPreferences.map((preference) => (
                        <Typography key={preference} className={classes.notificationSummaryItem}>
                          {preference}
                        </Typography>
                      ))
                    ) : (
                      <Typography className={classes.notificationSummaryItem}>
                        {t('No notification preferences')}
                      </Typography>
                    )}
                  </div>
                  <Typography className={data.footer.ok ? classes.statusOk : classes.statusWarn}>
                    {data.footer.text}
                  </Typography>
                  {data.canEdit && (
                    <div className={classes.cardActions}>
                      <Button
                        variant="contained"
                        size="small"
                        style={{ textTransform: 'none' }}
                        onClick={() => openEditContact(data.id)}
                      >
                        {t('Edit Contact')}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className={classes.contactCardEmpty} key={`${section.service}-${role.roleArea}`}>
                  <span
                    className={classes.roleTag}
                    style={{ backgroundColor: role.roleColorBg, color: role.roleColorFg }}
                  >
                    {role.roleIcon} {t(role.roleLabel)}
                  </span>
                  <Typography className={classes.emptyMessage}>
                    {t(section.emptyMessage, { service: t(getServiceLabel(section.service)) })}
                  </Typography>
                  {addContactButton()}
                </div>
              ),
            )}
          </div>
        </Panel>
      ))}

      <WhyItMatters>
        {t(
          'Each contact needs a verified email and phone, the right type (tire / mechanical, primary / secondary), and approval limits - or service stalls.',
        )}
      </WhyItMatters>

      <OpenFullButton label={t('Open full Contacts')} onClick={openFullContacts} />
    </>
  );
}

export default ContactsSubArea;
